import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useHttpClient } from '@hooks/utils/httpConfig';
import { API_ENDPOINTS } from '../../api/ApiEndpoints';
import { CostsRequestDto, CostFiltersResponse, CostListItemDto } from './types';
import { PageDto } from '@cloudclevr/components';

// Hook to fetch cost breakdown data
export function useCostBreakdownQuery({
  roadmapId,
  searchTerm = '',
  categories = [],
  enabled = true
}: {
  roadmapId?: string;
  searchTerm?: string;
  categories?: string[];
  enabled?: boolean;
}): UseQueryResult<PageDto<CostListItemDto>> {
  const client = useHttpClient();

  const fetchCosts = async (): Promise<PageDto<CostListItemDto>> => {
    if (!roadmapId) {
      throw new Error('Roadmap ID is required');
    }

    const requestBody: CostsRequestDto = {
      roadmapId,
      searchTerm,
      categories
    };

    try {
      const response = await client.post<PageDto<CostListItemDto>>(
        API_ENDPOINTS.ROADMAP_COSTS_PAGE(roadmapId),
        requestBody
      );
      if (response) {
        return response;
      }
      // return empty page shape if response is missing
      return {
        items: [],
        totalItemCount: 0,
        itemCount: 0,
        totalPageCount: 0,
        pageIndex: 0,
        pageSize: 0,
        currentPage: 0
      } as PageDto<CostListItemDto>;
    } catch (error: any) {
      // Handle 404 and return empty page for no results
      if (error?.response?.status === 404) {
        return {
          items: [],
          totalItemCount: 0,
          itemCount: 0,
          totalPageCount: 0,
          pageIndex: 0,
          pageSize: 0,
          currentPage: 0
        } as PageDto<CostListItemDto>;
      }
      throw error;
    }
  };

  return useQuery<PageDto<CostListItemDto>, Error>({ queryKey: ['costBreakdown', roadmapId, searchTerm, categories], queryFn: fetchCosts, 
      enabled: enabled && !!roadmapId,
      refetchOnWindowFocus: false,
      gcTime: 3000,
      staleTime: 1000,
    }
  );
}

// Hook to fetch cost breakdown filters
export function useCostBreakdownFiltersQuery(roadmapId?: string, enabled: boolean = true): UseQueryResult<CostFiltersResponse> {
  const client = useHttpClient();

  const fetchFilters = async (): Promise<CostFiltersResponse> => {
    if (!roadmapId) {
      throw new Error('Roadmap ID is required');
    }

    const response = await client.get<CostFiltersResponse>(
      API_ENDPOINTS.ROADMAP_COSTS_FILTER(roadmapId)
    );

    return response;
  };

  return useQuery<CostFiltersResponse, Error>({ queryKey: ['costBreakdownFilters', roadmapId], queryFn: fetchFilters, 
      enabled: enabled && !!roadmapId,
      refetchOnWindowFocus: false,
      gcTime: 10000,
      staleTime: 5000,
    }
  );
}